/**
 * Theme-/Skalierungssteuerung — Gegenstück zu Win11Theme::SetTheme(),
 * GetTheme(), IsDarkMode(), SetUiScalePercent()/GetUiScalePercent().
 */

export const APP_THEMES = ['dark', 'bright', 'light-blue', 'light-green'] as const;
export type AppTheme = (typeof APP_THEMES)[number];

/** Entspricht Win11Theme::ThemeDisplayName(). */
const THEME_DISPLAY_NAMES: Record<AppTheme, string> = {
  dark: 'Dark',
  bright: 'Bright',
  'light-blue': 'Light Blue',
  'light-green': 'Light Green',
};

/** Die 5 Stufen aus dem Help > Scaling-Menü des Originals. */
export const UI_SCALE_STEPS = [100, 125, 150, 175, 200] as const;
export type UiScalePercent = (typeof UI_SCALE_STEPS)[number];

type ThemeListener = (theme: AppTheme) => void;

let currentTheme: AppTheme = 'dark';
let currentScalePercent = 100;
const listeners = new Set<ThemeListener>();

export function themeDisplayName(theme: AppTheme): string {
  return THEME_DISPLAY_NAMES[theme];
}

export function getTheme(): AppTheme {
  return currentTheme;
}

/** true nur für 'dark' — Gegenstück zu Win11Theme::IsDarkMode(). */
export function isDarkMode(): boolean {
  return currentTheme === 'dark';
}

/**
 * Wendet ein Theme an (DOM + Listener). Persistiert NICHT selbst —
 * `Settings` (`src/app/settings.ts`, geschrieben vom `AppStore`) ist laut
 * CONTRACTS.md §6 der einzige Persistenzpunkt (Korrektur AP-1 → AP-6).
 */
export function setTheme(theme: AppTheme): void {
  currentTheme = theme;
  document.documentElement.dataset['theme'] = theme;
  syncMetaThemeColor();
  for (const listener of listeners) listener(theme);
}

export function onThemeChanged(listener: ThemeListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getUiScalePercent(): number {
  return currentScalePercent;
}

/** Wendet eine UI-Skalierung an (DOM). Persistiert NICHT selbst, siehe `setTheme()`. */
export function setUiScalePercent(percent: number): void {
  currentScalePercent = Math.min(200, Math.max(100, Math.round(percent)));
  document.documentElement.style.setProperty('--ui-scale', String(currentScalePercent / 100));
}

/** Liest den aktuell aufgelösten Wert eines Palette-Tokens (z. B. für Canvas-Rendering). */
export function themeColor(token: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim();
}

function syncMetaThemeColor(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = themeColor('root');
}

/**
 * Einmalig beim Start. Anders als ComputeDefaultUiScalePercent() im Original
 * wird hier NICHT invers zur Anzeigeskalierung geraten — im Browser macht das
 * bereits devicePixelRatio/Zoom, Default ist 100 %.
 *
 * Liest NICHT mehr selbst aus `localStorage` (Korrektur AP-1 → AP-6): der
 * Aufrufer (`main.ts`) übergibt Theme/Skalierung aus den bereits geladenen
 * `Settings` — `SETTINGS_STORAGE_KEY` ist der einzige Speicherort.
 */
export function initTheme(theme: AppTheme, uiScalePercent: number): void {
  setTheme(theme);
  setUiScalePercent(uiScalePercent);
}
